import { Activity, ArrowRight, CheckCircle2, Headphones, Heart, Plus, Radio, RefreshCw, Sparkles, TrendingUp, WalletCards } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import OrderCard from '../components/OrderCard';
import PageMotion from '../components/PageMotion';
import { money, number, shortDate } from '../format';
import type { DashboardData } from '../types';

export default function DashboardPage() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const result = await api<DashboardData>('/dashboard');
      setData(result);
      setUser(result.user);
      setError('');
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Ошибка загрузки'); }
  };
  useEffect(() => { load(); }, []);

  return (
    <PageMotion>
      <header className="page-header">
        <div><span className="eyebrow">Личный кабинет</span><h1>Главная</h1></div>
        <button className="icon-button" onClick={load} aria-label="Обновить"><RefreshCw size={18} /></button>
      </header>
      {error && <div className="inline-error">{error}</div>}
      <section className="balance-hero">
        <div className="balance-copy">
          <div className="balance-topline"><span>Основной баланс</span><i /> <b>Аккаунт активен</b></div>
          <span>Добрый день, {user?.name.split(' ')[0]}</span>
          <h2>{money(data?.user.balance ?? user?.balance ?? 0)}</h2>
          <p>Доступно для новых кампаний</p>
        </div>
        <div className="balance-actions">
          <Link className="light-button" to="/wallet"><Plus size={18} /> Пополнить</Link>
          <Link className="dark-button" to="/services"><Sparkles size={18} /> Новый заказ</Link>
        </div>
        <div className="hero-equalizer" aria-hidden="true">
          {Array.from({ length: 24 }, (_, index) => <i className={`hero-bar-${index % 8}`} key={index} />)}
        </div>
        <div className="hero-sheen" aria-hidden="true" />
      </section>
      <section className="metrics-row">
        <div className="metric-card"><span className="metric-icon tone-blue"><Activity size={19} /></span><div><span>Активные</span><strong>{data?.stats.active ?? '—'}</strong><small>сейчас в работе</small></div></div>
        <div className="metric-card"><span className="metric-icon tone-green"><CheckCircle2 size={19} /></span><div><span>Выполнено</span><strong>{data?.stats.completed ?? '—'}</strong><small>за всё время</small></div></div>
        <div className="metric-card"><span className="metric-icon tone-violet"><TrendingUp size={19} /></span><div><span>Объём</span><strong>{data ? number(data.stats.reach) : '—'}</strong><small>действий запущено</small></div></div>
      </section>
      <div className="dashboard-grid">
        <section className="content-section active-section">
          <div className="section-title"><div><span className="eyebrow">Сейчас</span><h2>Активные заказы</h2></div><Link to="/orders">Все <ArrowRight size={16} /></Link></div>
          <div className="stack-list">
            {!data ? <div className="loading-state"><span className="spinner" /> Загружаем заказы</div> : data.orders.filter((order) => ['processing', 'pending'].includes(order.status)).length ? data.orders.filter((order) => ['processing', 'pending'].includes(order.status)).slice(0, 3).map((order) => <OrderCard key={order.id} order={order} compact />) : <div className="empty-state"><div><Headphones size={24} /></div><h3>Активных заказов нет</h3><p>Выберите услугу и запустите продвижение.</p><Link to="/services">Перейти к услугам</Link></div>}
          </div>
        </section>
        <aside className="content-section quick-services">
          <div className="section-title"><div><span className="eyebrow">Быстрый старт</span><h2>Услуги</h2></div></div>
          <Link to="/services?category=plays"><span className="service-icon tone-orange"><Headphones size={19} /></span><div><b>Прослушивания</b><small>от 900 ₽</small></div><ArrowRight size={17} /></Link>
          <Link to="/services?category=listeners"><span className="service-icon tone-cyan"><Radio size={19} /></span><div><b>Слушатели</b><small>от 500 ₽</small></div><ArrowRight size={17} /></Link>
          <Link to="/services?category=album_likes"><span className="service-icon tone-pink"><Heart size={19} /></span><div><b>Лайки</b><small>от 500 ₽</small></div><ArrowRight size={17} /></Link>
          <Link to="/wallet"><span className="service-icon tone-violet"><WalletCards size={19} /></span><div><b>Баланс</b><small>История операций</small></div><ArrowRight size={17} /></Link>
        </aside>
      </div>
      <section className="activity-strip">
        <div className="section-title"><div><span className="eyebrow">История</span><h2>Последние операции</h2></div><Link to="/wallet">Подробнее</Link></div>
        {!data ? <div className="loading-state"><span className="spinner" /> Загрузка</div> : data.transactions.length ? data.transactions.map((item) => <div className="activity-row" key={item.id}><span className={item.amount >= 0 ? 'activity-plus' : 'activity-minus'}>{item.amount >= 0 ? '+' : '−'}</span><div><b>{item.title}</b><small>{shortDate(item.created_at)}</small></div><strong>{item.amount > 0 ? '+' : ''}{money(item.amount)}</strong></div>) : <p className="muted-text">Здесь появятся пополнения и покупки.</p>}
      </section>
    </PageMotion>
  );
}
